import React, { Suspense, lazy, useEffect } from 'react';
import { Outlet, Route, RouterProvider, Navigate, createBrowserRouter, createRoutesFromElements } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuthStore } from './hooks/useAuth';
import PrivateRoute from './components/PrivateRoute';
import AppLayout from './components/AppLayout';
import Fehlerseite from './components/Fehlerseite';
import PermissionRoute from './components/PermissionRoute';
import { AnsagerProvider } from './components/ui/Ansager';

const LoginPage = lazy(() => import('./features/LoginPage'));
const DashboardPage = lazy(() => import('./features/DashboardPage'));
const DeviceListPage = lazy(() => import('./features/DeviceListPage'));
const HealthOverviewPage = lazy(() => import('./features/HealthOverviewPage'));
const DeviceDetailPage = lazy(() => import('./features/DeviceDetailPage'));
const DeviceChatPage = lazy(() => import('./features/DeviceChatPage'));
const TenantsPage = lazy(() => import('./features/TenantsPage'));
const UserManagementPage = lazy(() => import('./features/UserManagementPage'));
const CustomerManagementPage = lazy(() => import('./features/CustomerManagementPage'));
const AccountPage = lazy(() => import('./features/AccountPage'));
const FieldServiceV2Page = lazy(() => import('./features/FieldServiceV2Page'));
const AssetRecordPage = lazy(() => import('./features/AssetRecordPage'));
const ReportPage = lazy(() => import('./features/ReportPage'));
const TechnicianModePage = lazy(() => import('./features/TechnicianModePage'));
const NotFoundPage = lazy(() => import('./features/NotFoundPage'));

const RouteLoader: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
    <div className="flex flex-col items-center gap-4 text-center">
      <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600"></div>
      <div>
        <p className="text-sm font-semibold text-slate-700">Ansicht wird geladen</p>
        <p className="text-xs text-slate-400">Die Seite wird gerade vorbereitet.</p>
      </div>
    </div>
  </div>
);

/**
 * Data-Router statt `BrowserRouter`.
 *
 * Der deklarative Router kennt `useBlocker` nicht - er wirft
 * "useBlocker must be used within a data router". Die Anlagenakte braucht ihn,
 * um einen Seitenwechsel mit ungespeicherten Aenderungen aufzuhalten;
 * `beforeunload` allein greift nur beim Schliessen des Fensters.
 *
 * Die Routen bleiben als JSX stehen (`createRoutesFromElements`), damit sich
 * die Struktur unveraendert lesen laesst. Neu ist nur, dass `Suspense` an einer
 * pfadlosen Elternroute haengt statt um `Routes` herum.
 *
 * **Zwei Fehlergrenzen (`errorElement`).** Scheitert eine Seite beim Aufbau,
 * war die Anwendung bis 2026-08-12 eine weisse Flaeche - ohne Text und ohne
 * Weg zurueck. Jetzt faengt die innere Grenze den Fehler *innerhalb* des
 * Rahmens ab: Menue und Seitenleiste bleiben stehen, man kann woanders
 * hingehen. Die aeussere greift, wenn schon der Rahmen selbst scheitert.
 */
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      errorElement={<Fehlerseite />}
      element={(
        <Suspense fallback={<RouteLoader />}>
          <Outlet />
        </Suspense>
      )}
    >
      <Route path="/login" element={<LoginPage />} />
      <Route path="/analysis/chat" element={<Navigate to="/devices" replace />} />

      <Route element={<PrivateRoute><AppLayout /></PrivateRoute>} errorElement={(
        <PrivateRoute>
          <AppLayout>
            <Fehlerseite imRahmen />
          </AppLayout>
        </PrivateRoute>
      )}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/devices" element={<DeviceListPage />} />
        <Route path="/anlagenzustand" element={<HealthOverviewPage />} />
        <Route path="/devices/:deviceId" element={<DeviceDetailPage />} />
        <Route path="/devices/:deviceId/chat" element={<PermissionRoute permission="useDeviceChat"><DeviceChatPage /></PermissionRoute>} />
        <Route path="/devices/:deviceId/asset" element={<PermissionRoute permission="editAssetRecords"><AssetRecordPage /></PermissionRoute>} />
        <Route path="/devices/:deviceId/report" element={<ReportPage />} />
        <Route path="/service-cases" element={<PermissionRoute permission="viewServiceCases"><FieldServiceV2Page /></PermissionRoute>} />
        <Route path="/service-cases-v2" element={<Navigate to="/service-cases" replace />} />
        <Route path="/customers" element={<PermissionRoute permission="manageCustomers"><CustomerManagementPage /></PermissionRoute>} />
        <Route path="/technician" element={<PermissionRoute permission="useTechnicianMode"><TechnicianModePage /></PermissionRoute>} />
        <Route path="/tenants" element={<PermissionRoute permission="manageTenants"><TenantsPage /></PermissionRoute>} />
        <Route path="/users" element={<PermissionRoute permission="manageTenantUsers"><UserManagementPage /></PermissionRoute>} />
        <Route path="/analysis" element={<Navigate to="/devices" replace />} />
        <Route path="/account" element={<AccountPage />} />
        {/* Innerhalb der Anmeldung: unbekannte Adresse benennen, statt
            still auf die Startseite umzuleiten. */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>,
  ),
);

const queryClient = new QueryClient();
const App: React.FC = () => {
  useEffect(() => {
    const { fetchMe } = useAuthStore.getState();
    fetchMe();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      {/* Der Ansager haengt aussen: Seine beiden Bereiche muessen im Dokument
          stehen, *bevor* irgendeine Meldung entsteht - sonst sagt ein
          Vorleseprogramm die erste Rueckmeldung nicht an. Siehe Ansager.tsx. */}
      <AnsagerProvider>
        <RouterProvider router={router} />
      </AnsagerProvider>
    </QueryClientProvider>
  );
};

export default App;
